import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useFormik } from "formik";
import { useTranslation } from "react-i18next";
import ReactSelect from "react-select";
import swal from "sweetalert";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  FolderTree,
  ImageIcon,
  LayoutGrid,
  Lock,
  List as ListIcon,
  Search,
  Sparkles,
  Tag,
  Trash2,
  Upload,
  FileText,
} from "lucide-react";

import Spinner from "../../Layouts/Spinner";
import FormField from "../../Common/FormField/FormField";
import { handleImageUpload } from "../../Common/ImageUpload/handleImageUpload";
import { nbSelectStyles } from "../../Common/FormShell/nbSelectStyles";
import { addsubcategary } from "../../Constant/constant";
import {
  addSubcategory,
  getSubcategoryByID,
  updateSubcategory,
} from "../../../redux/subcategory/action";
import { getAllCategorieslist } from "../../../redux/size/action";
import UnsavedChangesGuard from "../Category/sections/UnsavedChangesGuard";
import LandingPageSeoSection from "../../Common/LandingPageSeo/LandingPageSeoSection";
import PagePreview from "../../Common/LandingPageSeo/PagePreview";
import { crumbPath, metaDescriptionFallback, pageAddress } from "../../Common/LandingPageSeo/seoPreview";
import { landingPageValues, splitLandingPageValues } from "../../Common/LandingPageSeo/landingPageFields";
import {
  POSITION_MAX,
  POSITION_MIN,
  CATEGORY_STATUS,
} from "../Category/constants";
import { usePermissions } from "../../../Hooks/usePermissions";
import { MENU, PERMISSION } from "../../../lib/permissions";
import axiosInstance from "../../../redux/api";
import "../../Common/FormShell/FormShell.css";

const ACCEPTED_TYPES = "image/png,image/jpeg,image/webp,image/svg+xml";
const MAX_SIZE_MB = 5;

const PREVIEW_TABS = [
  { key: "storefront", label: "Storefront", Icon: LayoutGrid },
  { key: "page", label: "Page", Icon: FileText },
  { key: "listing", label: "Admin list", Icon: ListIcon },
  { key: "search", label: "Search", Icon: Search },
];

const INITIAL_VALUES = {
  title: "",
  ar_title: "",
  image: "",
  category_ids: [],
  allowed_types: [],
  // The SEO section edits the sub-category's storefront page; see landingPageFields.
  ...landingPageValues(null),
  status: CATEGORY_STATUS.ACTIVE,
  position: 1,
  home_view: true,
  gender: 1,
};

const slugify = (str = "") =>
  String(str)
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "");

const SubCategoryForm = ({ mode = "create", embeddedId, onDone, onCancel }) => {
  const isEdit = mode === "edit";
  const { id: routeId } = useParams();
  const id = embeddedId ?? routeId;
  const { t } = useTranslation();
  const navigate = useNavigate();
  // Drawer mode: save/cancel resolve to callbacks instead of route changes.
  const embedded = typeof onDone === "function";
  const saveNav = embedded ? () => onDone() : navigate;
  const finishSave = () => (embedded ? onDone() : navigate("/subcategory"));
  const finishCancel = () => (embedded ? onCancel?.() : navigate("/subcategory"));

  // Persist the sub-category ⇄ type links. Non-fatal: a failure warns but does
  // not block the sub-category save itself.
  const saveSubcategoryTypes = async (subCategoryId, typeIds) => {
    try {
      await axiosInstance.put("admin/types/sub-category-types", {
        sub_category_id: Number(subCategoryId),
        type_ids: typeIds,
      });
    } catch (err) {
      await swal({
        text:
          "Sub-category saved, but allowed types could not be saved: " +
          (err?.response?.data?.message || err.message),
        icon: "warning",
        button: "Close",
        timer: 3000,
      });
    }
  };
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewTab, setPreviewTab] = useState("storefront");
  const fileInputRef = useRef(null);

  // Allowed product types now live on the sub-category (moved off Category).
  const [typeOptions, setTypeOptions] = useState([]);
  const [typesLoading, setTypesLoading] = useState(true);
  const [typesError, setTypesError] = useState(null);

  const isEditable = isEdit
    ? hasAction(MENU.CATALOGUE, PERMISSION.EDIT)
    : true;
  const readOnly = isEdit && !isEditable;

  // Fetch parent category options
  useEffect(() => {
    dispatch(getAllCategorieslist({ search_key: "", status: "" }));
  }, [dispatch]);

  // Fetch subcategory record in edit mode
  useEffect(() => {
    if (isEdit && id) dispatch(getSubcategoryByID(id));
  }, [dispatch, id, isEdit]);

  // Fetch all available product types for the multi-select.
  useEffect(() => {
    const controller = new AbortController();
    axiosInstance
      .get("admin/types", {
        params: { page: 1, search_key: "", status: "", per_page: 500 },
        signal: controller.signal,
      })
      .then((res) => {
        const types = res.data?.response || [];
        setTypeOptions(types.map((typ) => ({ value: typ.id, label: typ.name })));
        setTypesError(null);
      })
      .catch((err) => {
        if (err.name === "CanceledError" || err.code === "ERR_CANCELED") return;
        setTypesError("Failed to load product types");
        setTypeOptions([]);
      })
      .finally(() => {
        if (!controller.signal.aborted) setTypesLoading(false);
      });
    return () => controller.abort();
  }, []);

  const categories = useSelector((state) => state.reusableSlice.catagoriesList);
  const selectedCustomer = useSelector(
    ({ subcategory: { subcategorybyid } }) => subcategorybyid,
  );

  const categoryOptions = useMemo(
    () =>
      Array.isArray(categories)
        ? categories.map((c) => ({ value: c.id, label: c.title }))
        : [],
    [categories],
  );

  // Guard against the Redux record lagging when switching targets in the
  // drawer: only seed once the loaded record matches the requested id.
  const recordMatches =
    !!selectedCustomer && Number(selectedCustomer.id) === Number(id);
  const initialValues = useMemo(() => {
    if (!isEdit || !recordMatches) return INITIAL_VALUES;
    return {
      title: selectedCustomer?.title || "",
      ar_title: selectedCustomer?.ar_title || "",
      image: selectedCustomer?.image || "",
      category_ids: Array.isArray(selectedCustomer?.categories)
        ? selectedCustomer.categories
            .map((c) => Number(c?.id))
            .filter((n) => Number.isInteger(n) && n > 0)
        : [],
      allowed_types: [], // populated by the linked-types loader below
      ...landingPageValues(selectedCustomer?.landing_page),
      status: selectedCustomer?.status ?? CATEGORY_STATUS.ACTIVE,
      position: selectedCustomer?.position || 1,
      home_view: selectedCustomer?.home_view ?? true,
      gender: selectedCustomer?.gender || 1,
    };
  }, [isEdit, recordMatches, selectedCustomer]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema: useMemo(() => addsubcategary(), []),
    onSubmit: async (values) => {
      if (readOnly) return;
      setSubmitting(true);
      // allowed_types is a sibling pivot — keep it out of the sub-category body.
      // The SEO copy goes to the sub-category's page, as `landing_page`.
      const { allowed_types, ...rest } = values;
      const subPayload = splitLandingPageValues(rest);
      const typeIds = (allowed_types || []).map((t) => t.value);

      if (isEdit) {
        const subId = selectedCustomer?.id ?? Number(id);
        // Save the type links first so the update action's navigation can't
        // race the pivot write.
        await saveSubcategoryTypes(subId, typeIds);
        dispatch(
          updateSubcategory(
            { ...subPayload, id: subId, image: values.image },
            setSubmitting,
            saveNav,
          ),
        );
      } else {
        // Suppress the action's own navigation (pass a no-op) so we can attach
        // types to the new record before leaving the form.
        const res = await dispatch(
          addSubcategory(subPayload, setSubmitting, () => {}),
        );
        const newId =
          res?.data?.response?.id ?? res?.data?.id ?? null;
        if (newId && typeIds.length > 0) {
          await saveSubcategoryTypes(newId, typeIds);
        }
        if (res?.data?.status) finishSave();
      }
    },
  });

  // Edit mode: once the record matches and the type list is loaded, pull this
  // sub-category's currently-linked types and seed the multi-select.
  useEffect(() => {
    if (!isEdit || !id || typeOptions.length === 0 || !recordMatches) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axiosInstance.get("admin/types/sub-category-types", {
          params: { sub_category_id: id },
        });
        if (cancelled) return;
        const ids = (res.data?.response || []).map((tt) =>
          typeof tt === "number" || typeof tt === "string"
            ? Number(tt)
            : tt.type_id ?? tt.id,
        );
        formik.setFieldValue(
          "allowed_types",
          typeOptions.filter((o) => ids.includes(o.value)),
        );
      } catch (err) {
        if (!cancelled) console.error("Failed to load linked types:", err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, id, typeOptions, recordMatches, selectedCustomer?.id]);

  const { values, errors, touched } = formik;
  const isDirty = formik.dirty && !submitting;
  const errorCount = Object.keys(errors).filter((k) => touched[k]).length;
  const imageUrl = values.image;
  const imageError = touched.image && errors.image;
  const isActive = Number(values.status) === CATEGORY_STATUS.ACTIVE;
  const visibleInHeader = Boolean(values.home_view);
  const selectedCategoryOptions = (Array.isArray(values.category_ids)
    ? values.category_ids
    : []
  )
    .map((id) =>
      categoryOptions.find((o) => String(o.value) === String(id)),
    )
    .filter(Boolean);
  const parentLabel =
    selectedCategoryOptions.map((o) => o.label).join(", ") || null;
  const parentSlug = selectedCategoryOptions[0]
    ? slugify(selectedCategoryOptions[0].label)
    : "category";

  const previewName =
    (values.title || "").trim() ||
    (isEdit ? selectedCustomer?.title : "Subcategory name") ||
    "Subcategory name";
  const previewArName = (values.ar_title || "").trim();
  const previewSlug = slugify(values.title) || "subcategory-slug";
  const landingPage = (recordMatches && selectedCustomer?.landing_page) || null;
  const previewMetaTitle =
    (values.lp_seo_title || "").trim() || `${previewName} | Nysmah`;
  const previewMetaDesc =
    (values.lp_meta_description || "").trim() || metaDescriptionFallback("en", previewName);
  const previewPath = pageAddress("en", landingPage, `${parentSlug}/${previewSlug}`);

  const processFile = async (file) => {
    if (!file || readOnly) return;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      formik.setFieldError("image", `Image must be under ${MAX_SIZE_MB}MB`);
      formik.setFieldTouched("image", true, false);
      return;
    }
    setUploading(true);
    try {
      await handleImageUpload(
        file,
        { title: "images", name: "image" },
        "image",
        formik,
      );
    } catch {
      formik.setFieldError("image", "Image upload failed. Try again.");
      formik.setFieldTouched("image", true, false);
    } finally {
      setUploading(false);
    }
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    processFile(file);
    e.target.value = "";
  };
  const onTileClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (readOnly) return;
    processFile(e.dataTransfer.files?.[0]);
  };

  const onDiscard = () => {
    if (
      isDirty &&
      !window.confirm(
        t("You have unsaved changes. Leave this page and discard them?"),
      )
    ) {
      return;
    }
    finishCancel();
  };

  // Embedded (drawer) mode: Esc runs the same guarded discard as the back button.
  useEffect(() => {
    if (!embedded) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onDiscard();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readOnly) return;
    const errs = await formik.validateForm();
    formik.setTouched(
      Object.keys(errs).reduce((acc, k) => ({ ...acc, [k]: true }), {}),
    );
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[name="${firstKey}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
        if (typeof el.focus === "function") el.focus({ preventScroll: true });
      }
      return;
    }
    formik.handleSubmit(e);
  };

  return (
    <div className="nb-page">
      <UnsavedChangesGuard when={isDirty} />

      <div className="nb-topbar">
        {embedded ? (
          <button type="button" className="nb-topbar__back" onClick={onDiscard}>
            <ChevronLeft size={14} aria-hidden="true" />
            {t("Subcategories")}
          </button>
        ) : (
          <Link to="/subcategory" className="nb-topbar__back">
            <ChevronLeft size={14} aria-hidden="true" />
            {t("Subcategories")}
          </Link>
        )}
        <ChevronRight size={12} className="nb-topbar__sep" aria-hidden="true" />
        <span className="nb-topbar__current">
          {isEdit
            ? selectedCustomer?.title || t("Edit subcategory")
            : t("New subcategory")}
        </span>
        {readOnly ? (
          <span
            className="nb-topbar__draft"
            style={{
              color: "var(--nb-muted)",
              background: "#f1f5f9",
              borderColor: "var(--nb-border-strong)",
            }}
          >
            <Lock size={11} aria-hidden="true" />
            {t("Read only")}
          </span>
        ) : (
          isDirty && (
            <span className="nb-topbar__draft">
              <span className="nb-topbar__dot" aria-hidden="true" />
              {isEdit ? t("Edited · unsaved") : t("Draft · unsaved")}
            </span>
          )
        )}
      </div>

      <form className="nb-shell" onSubmit={handleSubmit} noValidate>
        <div className="nb-form">
          <header className="nb-hero">
            <span className="nb-hero__eyebrow">
              <Sparkles size={12} aria-hidden="true" />
              {t("Catalog")}
            </span>
            <h1 className="nb-hero__title">
              {isEdit ? t("Edit subcategory") : t("Create a new subcategory")}
            </h1>
            <p className="nb-hero__sub">
              {t(
                "Subcategories live under a parent category and let shoppers narrow their browse path.",
              )}
            </p>
          </header>

          {errorCount > 0 && (
            <div className="nb-banner" role="alert">
              <AlertCircle size={16} aria-hidden="true" />
              <span>
                {errorCount === 1
                  ? t("1 field needs your attention.")
                  : t(`${errorCount} fields need your attention.`)}
              </span>
            </div>
          )}

          {/* 01 Identity */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">01</span>
              <div>
                <h2 className="nb-section__title">{t("Identity")}</h2>
                <p className="nb-section__hint">
                  {t("Bilingual subcategory name shown to shoppers.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="title"
                  label={t("Subcategory name (English)")}
                  required
                  touched={touched.title}
                  error={errors.title}
                >
                  <input
                    id="title"
                    name="title"
                    type="text"
                    className="nb-input"
                    placeholder={t("e.g. EDP")}
                    value={values.title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
                <FormField
                  name="ar_title"
                  label={t("Subcategory name (Arabic)")}
                  required
                  touched={touched.ar_title}
                  error={errors.ar_title}
                >
                  <input
                    id="ar_title"
                    name="ar_title"
                    type="text"
                    className="nb-input"
                    placeholder={t("الاسم بالعربية")}
                    dir="rtl"
                    lang="ar"
                    value={values.ar_title}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    autoComplete="off"
                    maxLength={120}
                    disabled={readOnly}
                  />
                </FormField>
              </div>
            </div>
          </section>

          {/* 02 Parent */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">02</span>
              <div>
                <h2 className="nb-section__title">
                  <FolderTree size={14} aria-hidden="true" style={{ marginRight: 6, opacity: 0.7 }} />
                  {t("Parent category")}
                </h2>
                <p className="nb-section__hint">
                  {t("Pick the top-level category this subcategory sits under.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <FormField
                name="category_ids"
                label={t("Belongs to")}
                required
                touched={touched.category_ids}
                error={errors.category_ids}
              >
                <ReactSelect
                  inputId="category_ids"
                  name="category_ids"
                  isMulti
                  options={categoryOptions}
                  styles={nbSelectStyles}
                  placeholder={t("Select one or more categories…")}
                  isDisabled={readOnly}
                  value={selectedCategoryOptions}
                  onChange={(opts) => {
                    const ids = Array.from(
                      new Set(
                        (opts || [])
                          .map((o) => Number(o.value))
                          .filter((n) => Number.isInteger(n) && n > 0),
                      ),
                    );
                    formik.setFieldValue("category_ids", ids);
                  }}
                  onBlur={() => formik.setFieldTouched("category_ids", true)}
                  menuPortalTarget={document.body}
                />
              </FormField>
            </div>
          </section>

          {/* 03 Allowed product types */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">03</span>
              <div>
                <h2 className="nb-section__title">
                  <Tag size={14} aria-hidden="true" style={{ marginRight: 6, opacity: 0.7 }} />
                  {t("Allowed product types")}
                </h2>
                <p className="nb-section__hint">
                  {t(
                    "Product types customers can filter within this sub-category. Products inherit their type from here.",
                  )}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <FormField
                name="allowed_types"
                label={t("Product types")}
                hint={
                  typesError
                    ? t(typesError)
                    : t("Pick the types that products under this sub-category can use.")
                }
              >
                <ReactSelect
                  inputId="allowed_types"
                  isMulti
                  options={typeOptions}
                  isLoading={typesLoading}
                  isDisabled={Boolean(typesError) || readOnly}
                  styles={nbSelectStyles}
                  placeholder={t("Select types…")}
                  value={values.allowed_types}
                  onChange={(selected) =>
                    formik.setFieldValue("allowed_types", selected || [])
                  }
                  menuPortalTarget={document.body}
                />
              </FormField>
            </div>
          </section>

          {/* 04 Image */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">04</span>
              <div>
                <h2 className="nb-section__title">{t("Cover image")}</h2>
                <p className="nb-section__hint">
                  {t("Used on the storefront tile under the parent category page.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div
                className={`nb-drop ${dragOver ? "is-drag" : ""} ${
                  imageError ? "is-error" : ""
                } ${imageUrl ? "has-image" : ""}`}
                onClick={onTileClick}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    onTileClick();
                  }
                }}
                onDrop={onDrop}
                onDragOver={(e) => {
                  e.preventDefault();
                  if (!readOnly) setDragOver(true);
                }}
                onDragLeave={() => setDragOver(false)}
                role="button"
                tabIndex={readOnly ? -1 : 0}
                aria-label={t("Upload subcategory image")}
                aria-disabled={readOnly}
              >
                {imageUrl ? (
                  <>
                    <img src={imageUrl} alt={t("Subcategory cover")} className="nb-drop__img" />
                    {!readOnly && (
                      <div className="nb-drop__overlay">
                        <Upload size={18} aria-hidden="true" />
                        <span>{t("Replace image")}</span>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="nb-drop__empty">
                    <span className="nb-drop__bubble">
                      {uploading ? <Spinner /> : <ImageIcon size={20} aria-hidden="true" />}
                    </span>
                    <div className="nb-drop__title">
                      {uploading ? t("Uploading…") : t("Drop a cover image or click to upload")}
                    </div>
                    <div className="nb-drop__hint">
                      {t("PNG, JPG, WEBP or SVG · up to 5MB")}
                    </div>
                  </div>
                )}
                <input
                  ref={fileInputRef}
                  id="image"
                  name="image"
                  type="file"
                  accept={ACCEPTED_TYPES}
                  onChange={onPickFile}
                  hidden
                  disabled={uploading || readOnly}
                />
              </div>
              {imageUrl && !readOnly && (
                <div className="nb-thumbrow">
                  <button type="button" className="nb-btn nb-btn--ghost nb-btn--sm" onClick={onTileClick}>
                    <Upload size={13} aria-hidden="true" />
                    {t("Replace")}
                  </button>
                  <button
                    type="button"
                    className="nb-btn nb-btn--danger-ghost nb-btn--sm"
                    onClick={() => formik.setFieldValue("image", "")}
                  >
                    <Trash2 size={13} aria-hidden="true" />
                    {t("Remove")}
                  </button>
                </div>
              )}
              {imageError && (
                <p className="nb-fielderror" role="alert">
                  {t(errors.image)}
                </p>
              )}
            </div>
          </section>

          {/* 05 Visibility */}
          <section className="nb-section">
            <div className="nb-section__head">
              <span className="nb-section__step">05</span>
              <div>
                <h2 className="nb-section__title">{t("Visibility")}</h2>
                <p className="nb-section__hint">
                  {t("Where this subcategory appears, in what order, and whether it's live.")}
                </p>
              </div>
            </div>
            <div className="nb-section__body">
              <div className="nb-grid-2">
                <FormField
                  name="status"
                  label={t("Status")}
                  touched={touched.status}
                  error={errors.status}
                >
                  <div className="nb-toggle" role="radiogroup" aria-label={t("Status")}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={isActive}
                      className={`nb-toggle__opt ${isActive ? "is-on" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.ACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--on" />
                      {t("Active")}
                    </button>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={!isActive}
                      className={`nb-toggle__opt ${!isActive ? "is-off" : ""}`}
                      onClick={() => formik.setFieldValue("status", CATEGORY_STATUS.INACTIVE)}
                      disabled={readOnly}
                    >
                      <span className="nb-toggle__dot nb-toggle__dot--off" />
                      {t("Inactive")}
                    </button>
                  </div>
                </FormField>

                <FormField
                  name="position"
                  label={t("Position")}
                  hint={t("Lower numbers appear first.")}
                  touched={touched.position}
                  error={errors.position}
                >
                  <div className="nb-stepper">
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.max(POSITION_MIN, Number(values.position || 1) - 1),
                        )
                      }
                      aria-label={t("Decrease")}
                    >
                      −
                    </button>
                    <input
                      id="position"
                      name="position"
                      type="number"
                      inputMode="numeric"
                      min={POSITION_MIN}
                      max={POSITION_MAX}
                      className="nb-input nb-input--center"
                      value={values.position}
                      onChange={(e) => {
                        const v = e.target.value;
                        formik.setFieldValue("position", v === "" ? "" : Number(v));
                      }}
                      onBlur={formik.handleBlur}
                      disabled={readOnly}
                    />
                    <button
                      type="button"
                      className="nb-stepper__btn"
                      disabled={readOnly}
                      onClick={() =>
                        formik.setFieldValue(
                          "position",
                          Math.min(POSITION_MAX, Number(values.position || 0) + 1),
                        )
                      }
                      aria-label={t("Increase")}
                    >
                      +
                    </button>
                  </div>
                </FormField>
              </div>

              <label className="nb-switchrow">
                <input
                  id="home_view"
                  name="home_view"
                  type="checkbox"
                  checked={visibleInHeader}
                  onChange={(e) =>
                    formik.setFieldValue("home_view", e.target.checked)
                  }
                  disabled={readOnly}
                />
                <span className="nb-switchrow__track" aria-hidden="true">
                  <span className="nb-switchrow__thumb" />
                </span>
                <span className="nb-switchrow__text">
                  <strong>{t("Show in main navigation")}</strong>
                  <span>
                    {t("Surface this subcategory in the storefront mega-menu under its parent.")}
                  </span>
                </span>
              </label>
            </div>
          </section>

          {/* 06 SEO */}
          <LandingPageSeoSection
            formik={formik}
            readOnly={readOnly}
            step="06"
            kind="subcategory"
            landingPage={landingPage}
            previewName={previewName}
            previewArName={previewArName}
          />
        </div>

        <aside className="nb-preview">
          <div className="nb-preview__sticky">
            <div className="nb-preview__head">
              <span className="nb-preview__eyebrow">
                <span className="nb-preview__pulse" aria-hidden="true" />
                {t("Live preview")}
              </span>
              <div className="nb-preview__tabs" role="tablist">
                {PREVIEW_TABS.map(({ key, label, Icon }) => (
                  <button
                    key={key}
                    type="button"
                    role="tab"
                    aria-selected={previewTab === key}
                    className={`nb-preview__tab ${previewTab === key ? "is-active" : ""}`}
                    onClick={() => setPreviewTab(key)}
                  >
                    <Icon size={13} aria-hidden="true" />
                    {t(label)}
                  </button>
                ))}
              </div>
            </div>

            <div className="nb-preview__stage">
              {previewTab === "storefront" && (
                <div className="nb-stage nb-stage--storefront">
                  <div className="nb-store-frame">
                    <div className="nb-store-frame__chrome">
                      <span /> <span /> <span />
                      <em>nysmah.com / {parentSlug}</em>
                    </div>
                    <div className="nb-store-card">
                      <span className="nb-store-card__parent">
                        {parentLabel ? `${parentLabel} ›` : t("Pick a parent →")}
                      </span>
                      <div className={`nb-store-card__media ${imageUrl ? "" : "is-empty"}`}>
                        {imageUrl ? (
                          <img src={imageUrl} alt={previewName} />
                        ) : (
                          <span className="nb-store-card__placeholder">
                            {previewName.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div className="nb-store-card__body">
                        <h3 className="nb-store-card__name">{previewName}</h3>
                        {previewArName && (
                          <p className="nb-store-card__sub">{previewArName}</p>
                        )}
                        <span className="nb-store-card__cta">
                          {t("View subcategory")}
                          <ChevronRight size={13} aria-hidden="true" />
                        </span>
                      </div>
                      {!isActive && (
                        <span className="nb-store-card__hidden">
                          {t("Hidden — won't appear on storefront")}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "page" && (
                <div className="nb-stage nb-stage--page">
                  <PagePreview
                    path={previewPath}
                    currentPath={landingPage?.path}
                    en={{
                      name: previewName,
                      heading: (values.lp_h1 || "").trim() || previewName,
                      html: values.lp_intro_html,
                    }}
                    ar={{
                      name: previewArName || previewName,
                      heading: (values.lp_ar_h1 || "").trim() || previewArName || previewName,
                      html: values.lp_ar_intro_html,
                    }}
                  />
                </div>
              )}
              {previewTab === "listing" && (
                <div className="nb-stage nb-stage--listing">
                  <div className="nb-listing-frame">
                    <div className="nb-listing-head">
                      <span>{t("Subcategory")}</span>
                      <span>{t("Status")}</span>
                      <span>{t("Parent")}</span>
                    </div>
                    <div className="nb-listing-row">
                      <div className="nb-listing-row__brand">
                        <div className={`nb-listing-avatar ${imageUrl ? "" : "is-empty"}`}>
                          {imageUrl ? (
                            <img src={imageUrl} alt={previewName} />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </div>
                        <div className="nb-listing-row__info">
                          <strong>{previewName}</strong>
                          <span className="nb-listing-row__pos">
                            #{values.position || POSITION_MIN}
                          </span>
                        </div>
                      </div>
                      <div className="nb-listing-row__col">
                        <span
                          className={`nb-badge ${
                            isActive ? "nb-badge--on" : "nb-badge--off"
                          }`}
                        >
                          <span className="nb-badge__dot" aria-hidden="true" />
                          {isActive ? t("Active") : t("Inactive")}
                        </span>
                      </div>
                      <div className="nb-listing-row__col nb-listing-row__col--muted">
                        {parentLabel || t("—")}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {previewTab === "search" && (
                <div className="nb-stage nb-stage--search">
                  <div className="nb-search-frame">
                    <div className="nb-search-head">
                      <Search size={12} aria-hidden="true" />
                      <span>nysmah</span>
                    </div>
                    <div className="nb-search-result">
                      <div className="nb-search-result__url">
                        <span className="nb-search-result__favicon">
                          {imageUrl ? (
                            <img src={imageUrl} alt="" />
                          ) : (
                            <span>{previewName.charAt(0).toUpperCase()}</span>
                          )}
                        </span>
                        <div>
                          <span className="nb-search-result__site">nysmah.com</span>
                          <span className="nb-search-result__path">
                            {crumbPath(previewPath)}
                          </span>
                        </div>
                      </div>
                      <h4 className="nb-search-result__title">{previewMetaTitle}</h4>
                      <p className="nb-search-result__desc">{previewMetaDesc}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="nb-preview__caption">
              {previewTab === "storefront" && t("Tile shown under the parent category page.")}
              {previewTab === "page" && t("The page as the shopper sees it, with the score for Google.")}
              {previewTab === "listing" && t("Row shown in the admin Subcategory List.")}
              {previewTab === "search" && t("Snippet on Google results — uses Meta fields.")}
            </div>
          </div>
        </aside>

        {!readOnly && (
          <div className={`nb-actionbar ${isDirty || submitting ? "is-visible" : ""}`}>
            <div className="nb-actionbar__inner">
              <span className="nb-actionbar__status">
                {submitting ? (
                  <>
                    <span className="nb-actionbar__spin" />
                    {isEdit ? t("Saving changes…") : t("Creating subcategory…")}
                  </>
                ) : errorCount > 0 ? (
                  <>
                    <AlertCircle size={14} aria-hidden="true" />
                    {errorCount === 1
                      ? t("1 error to fix before saving")
                      : t(`${errorCount} errors to fix before saving`)}
                  </>
                ) : (
                  <>
                    <span className="nb-actionbar__dot" aria-hidden="true" />
                    {t("Unsaved changes")}
                  </>
                )}
              </span>
              <div className="nb-actionbar__btns">
                <button
                  type="button"
                  className="nb-btn nb-btn--ghost"
                  onClick={onDiscard}
                  disabled={submitting}
                >
                  {t("Discard")}
                </button>
                <button
                  type="submit"
                  className="nb-btn nb-btn--primary"
                  disabled={submitting}
                >
                  {submitting ? (
                    <Spinner />
                  ) : isEdit ? (
                    t("Save changes")
                  ) : (
                    t("Create subcategory")
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </form>
    </div>
  );
};

export default SubCategoryForm;
